/* eslint-disable react-hooks/exhaustive-deps */
// Header.js
import styled from "styled-components";
import { useEffect, useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { AuthContext } from "../context/AuthContext";
import { MdError, MdInfo, MdCheckCircle } from "react-icons/md";
import { FiMenu } from "react-icons/fi";
import { API_BASE_URL } from "../config";

// Mapa rota → título (ajuste conforme suas rotas reais)
const ROUTE_TITLES = {
  "/dashboard": "Painel de Controle",
  "/backups": "Meus Backups",
  "/usuarios": "Usuários",
  "/fatura": "Faturas",
  "/": "Login",
};

export const Header = ({ onLogout, title, onToggleSidebar }) => {
  const { token, userEmail } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unseenNotifications, setUnseenNotifications] = useState([]);

  // título dinâmico baseado na URL atual; usa a prop "title" como fallback
  const computedTitle = (() => {
    // tenta match por prefixo (cobre rotas com params, ex: /backups/123)
    const matchKey = Object.keys(ROUTE_TITLES).find((k) =>
      location.pathname.startsWith(k)
    );
    return title || (matchKey ? ROUTE_TITLES[matchKey] : "Painel de Controle");
  })();

  useEffect(() => {
    const checkTokenExpiration = () => {
      if (!token) return;
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < currentTime) {
          Swal.fire({
            icon: "warning",
            title: "Sessão expirada",
            text: "Sua sessão expirou. Faça login novamente.",
            confirmButtonText: "OK",
            theme: "dark",
          }).then(() => {
            onLogout();
            navigate("/");
          });
        }
      } catch (err) {
        console.error("Erro ao verificar validade do token:", err);
      }
    };

    checkTokenExpiration();
    fetchNotifications();
    fetchUnseenNotifications();

    const tokenInterval = setInterval(checkTokenExpiration, 30000);
    const notifInterval = setInterval(() => {
      fetchNotifications();
      fetchUnseenNotifications();
    }, 4000);

    return () => {
      clearInterval(tokenInterval);
      clearInterval(notifInterval);
    };
  }, [token, onLogout, navigate]);

  // (opcional) sincroniza o <title> do documento com o Header
  useEffect(() => {
    document.title = computedTitle;
  }, [computedTitle]);

  const fetchUnseenNotifications = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_unseen_notifications.php`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (data.unseen) {
        setUnseenNotifications(data.unseen);
      }
    } catch (err) {
      console.error("Erro ao buscar notificações não visualizadas:", err);
    }
  };

  const handleNotificationClick = async () => {
    setShowNotifications(!showNotifications);

    // Se havia notificações não visualizadas, marcar como vistas
    if (!showNotifications && unseenNotifications.length > 0) {
      try {
        const res = await fetch(
          `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/mark_notifications_seen.php`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ids: unseenNotifications.map((n) => n.ID_NOTIFICATION),
            }),
          }
        );
        const data = await res.json();
        if (data.sucesso) {
          setUnseenNotifications([]); // Remover bolinha
        }
      } catch (err) {
        console.error("Erro ao marcar notificações como lidas:", err);
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/alterdata_backupsapp/apirest/aws/list_notifications.php`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (data.notifications) {
        setNotifications(data.notifications);
      }
    } catch (err) {
      console.error("Erro ao buscar notificações:", err);
    }
  };

  const renderIconByType = (type) => {
    switch (type) {
      case 0:
        return <MdError color="#e11d48" size={20} />;
      case 1:
        return <MdCheckCircle color="#22c55e" size={20} />;
      case 2:
      default:
        return <MdInfo color="#3b82f6" size={20} />;
    }
  };

  const formatDateInMessage = (msg) => {
    if (!msg) return "";
    // casa 2025-09-12 00:33:08 ou 2025-09-12T00:33:08
    // (global: converte todas as ocorrências)
    return msg.replace(
      /\b(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})\b/g,
      (_, y, m, d, hh, mm, ss) => `${d}/${m}/${y} ${hh}:${mm}:${ss}`
    );
  };

  return (
    <Container>
      <Hamburger onClick={onToggleSidebar} aria-label="Abrir menu">
        <FiMenu size={22} />
      </Hamburger>

      <Title>{computedTitle}</Title>

      <RightGroup>
        <UserInfo>{userEmail && `Logado como: ${userEmail}`}</UserInfo>
        <NotificationButton onClick={handleNotificationClick}>
          🔔
          {unseenNotifications.length > 0 && <NotificationDot />}
        </NotificationButton>

        {showNotifications && (
          <NotificationPanel>
            <h4 style={{ marginTop: "unset" }}>Notificações</h4>
            {notifications.length === 0 ? (
              <p style={{ color: "#aaa", fontSize: "0.9rem" }}>
                Nenhuma notificação
              </p>
            ) : (
              notifications.map((notif) => (
                <NotificationItem key={notif.ID_NOTIFICATION}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                    }}
                  >
                    {renderIconByType(Number(notif.TYPE))}
                    <NotificationTitle>{notif.TITLE}</NotificationTitle>
                  </div>
                  <NotificationMessage>
                    {formatDateInMessage(notif.MESSAGE)}
                  </NotificationMessage>
                  <small style={{ color: "#888" }}>
                    {new Date(notif.CREATED_AT).toLocaleString("pt-BR")}
                  </small>
                </NotificationItem>
              ))
            )}
          </NotificationPanel>
        )}

        <LogoutButton onClick={onLogout}>Sair</LogoutButton>
      </RightGroup>
    </Container>
  );
};

// =================== STYLES ===================

const Container = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  height: 60px;
  background: #1a1a1a;
  color: white;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 1px solid #2e2e2e;
  z-index: 1100;

  width: 100%;
  box-sizing: border-box;

  /* evita “piscar/sumir” em iOS/Android */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  backface-visibility: hidden;

  @media (max-width: 768px) {
    padding: 0 0.75rem;
  }
`;

const Hamburger = styled.button`
  display: none;
  @media (max-width: 760px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 0;
    color: #fff;
    cursor: pointer;
    padding: 0.25rem;
    margin-right: 0.5rem; /* opcional */
  }
`;

const RightGroup = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Title = styled.h1`
  font-size: 1.3rem;
`;

const LogoutButton = styled.button`
  background: #e11d48;
  color: #fff;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #be123c;
  }
`;

const UserInfo = styled.span`
  font-size: 0.9rem;
  color: #ccc;
- margin-left: auto;
- margin-right: 1rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

const NotificationButton = styled.button`
  background: none;
  color: white;
  font-size: 1.4rem;
  border: none;
  cursor: pointer;
  position: relative;
`;

const NotificationDot = styled.span`
  position: absolute;
  top: -2px;
  right: 0px;
  width: 10px;
  height: 10px;
  background: #ef4444;
  border-radius: 50%;
  border: 2px solid #1a1a1a;
`;

const NotificationPanel = styled.div`
  position: absolute;
  top: 55px;
  right: 2rem;
  width: 320px;
  background: #1f1f1f;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 1rem;
  z-index: 1000;
  color: white;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
`;

const NotificationItem = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #333;
`;

const NotificationTitle = styled.strong`
  font-size: 0.95rem;
  color: #fff;
`;

const NotificationMessage = styled.p`
  font-size: 0.85rem;
  color: #ccc;
  margin: 0.2rem 0 0.4rem;
`;
